import React, {useEffect, useMemo, useState} from "react";
import {Link} from "react-router-dom";
import SearchInput from "@source/components/UI/inputs/searchInput/searchInput";
import SelectInput, {ISelectItem} from "@source/components/UI/inputs/selectInput/selectInput";
import Svg from "@source/components/UI/svg/svg";
import {IFillVariant} from "@source/utils/enums";
import {HOME_ROUTES} from "@source/utils/utils";
import ConsentController from "@source/api/controllers/consentController";
import {ConsentCategory, ConsentCategoryQuery, ConsentCategorySource, ConsentCategoryStatus} from "@source/api/models/consentModels";
import useIncrementalConsentList from "@source/hooks/useIncrementalConsentList";
import {ConsentAutoLoad, ConsentFilterBar, ConsentRequestState} from "@source/components/consentDocuments";
import iconEditResident from "@source/assets/icons/iconEditResident.svg";
import {formatConsentDate} from "@source/components/consentDocuments/consentDate";
import {categoryPath, createCategoryListQuery, normalizeCategoryOrder, roleLabel, statusItems} from "./categoryHelpers";
import styles from "./categoryLibrary.module.scss";
import { useHistory } from 'react-router-dom';


const CategoryStatus = () => <span className={`${styles.status} ${styles.statusArchived}`}>Archived</span>;

export const CategoryLibrary = ({organizationId, canManage}: {organizationId: string; canManage: boolean}) => {
  const history = useHistory();
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(ConsentCategoryStatus.ACTIVE);
  useEffect(() => {
    const timeout = window.setTimeout(() => setSearch(searchText), 500);
    return () => window.clearTimeout(timeout);
  }, [searchText]);
  const filters = useMemo(() => createCategoryListQuery(status, search), [search, status]);
  const request = useMemo(() => (query: ConsentCategoryQuery & {page: number; limit: number}) => ConsentController.listCategories(organizationId, query), [organizationId]);
  const list = useIncrementalConsentList<ConsentCategory, ConsentCategoryQuery>(request, filters);
  const clearFilters = () => { setSearchText(""); setSearch(""); setStatus(ConsentCategoryStatus.ACTIVE); };
  const hasFilters = Boolean(searchText || status !== ConsentCategoryStatus.ACTIVE);
  return <>
    <ConsentFilterBar onClear={hasFilters ? clearFilters : undefined}>
      <SearchInput id="category-search" className={styles.search} value={searchText} onChange={(event) => setSearchText(event.target.value)} placeHolder="Search" fill={IFillVariant.Fill}/>
      <SelectInput id="category-status" className={styles.filterControl} value={status} items={statusItems} handleChange={(event) => setStatus(event.target.value as ConsentCategoryStatus)}/>
    </ConsentFilterBar>
    {list.isLoading && !list.items.length ? <ConsentRequestState variant="loading" title="Loading Consent Categories"/> : null}
    {list.error && !list.items.length ? <ConsentRequestState variant="error" title="Unable to load Consent Categories" description={list.error} onRetry={list.retry}/> : null}
    {!list.isLoading && !list.error && !list.items.length ? <ConsentRequestState variant="empty" title={hasFilters ? "No matching Consent Categories" : "No Consent Categories yet"} description={hasFilters ? "Try changing or clearing your filters." : "Create a Consent Category to define document source and signing rules."}/> : null}
    {list.items.length ? <div className={styles.list}>{list.items.map((category) => <CategoryCard key={category._id} category={category} canManage={canManage}/>)}</div> : null}
    {list.error && list.items.length ? <ConsentRequestState variant="error" title="Unable to load more Consent Categories" description={list.error} onRetry={list.retry}/> : null}
    {list.items.length ? <ConsentAutoLoad hasMore={list.hasMore} isLoading={list.isLoading} error={list.error} onLoadMore={list.loadMore}/> : null}
  </>;
};

const CategoryCard = ({category, canManage}: {category: ConsentCategory; canManage: boolean}) => <article className={styles.card}>
  <Link className={styles.cardLink} to={categoryPath(category._id)} aria-label={`Open Consent Category: ${category.name}`}>
    <div className={styles.cardMain}>
      <h3>{category.name}<span className={styles.source}> - {category.source === ConsentCategorySource.TEMPLATE ? "Template" : "Existing PDF"}</span></h3>
      <div className={styles.cardMeta}>
        <div className={styles.roleTags}>{category.requiredSignerRoles.map((role) => <span key={role}>{roleLabel(role)}</span>)}</div>
        <span className={styles.orderText}>{category.signingOrderRequired ? `Role order: ${normalizeCategoryOrder(category.requiredSignerRoles, category.signingOrder).map(roleLabel).join(" → ")}` : "Any signing order"}</span>
      </div>
    </div>
    <div className={styles.cardAside}>
      <time dateTime={category.updatedAt || category.createdAt}>Updated {formatConsentDate(category.updatedAt || category.createdAt)}</time>
      {category.status === ConsentCategoryStatus.ARCHIVED ? <CategoryStatus/> : null}
      {canManage && category.status === ConsentCategoryStatus.ACTIVE ? <div className={styles.editFamilyMember}>
        <Svg className={styles.icon} src={iconEditResident} color="#3956C7" width={16} height={16}/>
        <span className={styles.text}>Edit</span>
      </div> : null}
    </div>
  </Link>
</article>;

